import {
  Component,
  effect,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  model,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild
} from '@angular/core';
import { FormBuilder, FormGroup } from "@angular/forms";
import { LatLng, LatLngBoundsExpression, LatLngLiteral } from "leaflet";
import { debounceTime } from "rxjs";
import { QueryFormLocationInterface, QueryFormRequestInterface, StoreViewInterface } from "../models/store";
import { HttpLocationService } from "../services/http/location.service";
import { CoordinatesChangedInterface, OpenstreetmapComponent } from "./openstreetmap.component";
import { ScreenSizeService } from "../services/screen-size.service";
import { isPlatformBrowser } from "@angular/common";
import { PageService } from '@gea-waldviertler-website/shop-lib';
import { Router } from "@angular/router";
import { DistinctProductInterfaceType } from "@gea-waldviertler-website/gea-models";

@Component({
    selector: 'gea-shop-location-finder',
    templateUrl: './location-finder.component.html',
    standalone: false
})
export class LocationFinderComponent implements OnInit, OnDestroy {

  customerLocation?: QueryFormLocationInterface
  queryForm: FormGroup
  queryAddresses: QueryFormLocationInterface[] = []
  selectedQueryAddressesIndex: number = 0
  currentPosition?: LatLng
  showMap: boolean = false
  leafletLib: any
  private leafletReady!: Promise<any>
  instructionText: string = ''
  currentDate: Date = new Date()
  dateInterval?: number
  locatorLoading: boolean = false
  latestLeafletBounds?: LatLngBoundsExpression

  @Input() selectedLocation?: StoreViewInterface
  public locations = model<StoreViewInterface[]>([])
  @Input() distinctProduct?: DistinctProductInterfaceType
  @Input() quantity: number = 1
  @Input() displayStoreOverlay?: string = 'none'
  @Input() h1Header?: string = undefined

  @ViewChild('mapComponent') mapComponent!: OpenstreetmapComponent;
  @Output("selectStoreLocation") selectLocationEvent = new EventEmitter<StoreViewInterface>()

  /**
   * defining a ViewChild as a setter calls the method when the ElementRef is available
   * this is like an on load listener
   *
   * @param ElementRef<HTMLElement> el
   */
  @ViewChild('locationList', { read: ElementRef }) public locationList!: ElementRef<HTMLDivElement>

  storePickupEnabled: boolean = true

  constructor(
    @Inject(HttpLocationService) private locationService: HttpLocationService,
    @Inject(ScreenSizeService) private screenSizeService: ScreenSizeService,
    @Inject(PLATFORM_ID) private platformId: object,
    @Inject(PageService) private pageService: PageService,
    fb: FormBuilder,
    private router: Router
  ) {
    this.queryForm = fb.group({
      query: [],
    })
    this.queryForm.valueChanges.pipe(
      debounceTime(400)).subscribe((formData: QueryFormRequestInterface) => {
        this.searchAddress(formData)
    })

    effect(() => {
      const bs = this.pageService.getBootstrapSignal()()
      if (! bs) return
      this.storePickupEnabled = bs.config.storePickupEnabled
    })
  }

  ngOnInit(): void {
    if (this.distinctProduct) {
      this.instructionText = 'Prüfe hier die Verfügbarkeit oder Reservierbarkeit des Produktes in allen GEA Fillialen in Deiner Nähe.'
    } else {
      this.instructionText = 'Wähle deine gewünschte Filiale aus, um mehr über sie zu erfahren.'
    }

    this.screenSizeService.getDimensionChangeSubject().subscribe((width) => {
      if (this.screenSizeService.currentDimensionIsLargerThanEqual('md')) {
        this.showMap = true
        if (this.mapComponent) {
          if (this.customerLocation) {
            this.mapComponent.setCustomerLocation(this.customerLocation)
            if (this.latestLeafletBounds) {
              this.mapComponent.leafletMap.fitBounds(this.latestLeafletBounds)
            }
          }
        }
      } else {
        this.showMap = false
      }
    })

    if (isPlatformBrowser(this.platformId)) {
      this.leafletReady = import('leaflet').then((L: any) => {
        this.leafletLib = L.default || L
        return this.leafletLib
      })
      this.dateInterval = window.setInterval(() => {this.currentDate = new Date()}, 5000)
      if (this.screenSizeService.currentDimensionIsLargerThanEqual('md')) {
        this.showMap = true
      }
    }
  }
  
  inStockMessage() {
    if (! this.distinctProduct) return ''

    let message = this.distinctProduct.product.getDeliveryTime(
      this.distinctProduct.selectedVariant,
      this.distinctProduct.distinctId,
      this.distinctProduct.qualityId
    )?.text

    if (! message) {
      return 'Bald Verfügbar'
    }

    if (message.endsWith('Werktage')) {
      return ' In ' + message + 'n verfügbar'
    }

    return ' In ' + message + ' verfügbar'
  }

  inStockClass() {
    if (! this.distinctProduct) return ''

    let dt = this.distinctProduct.product.getDeliveryTime(
      this.distinctProduct.selectedVariant,
      this.distinctProduct.distinctId,
      this.distinctProduct.qualityId
    )

    if (! dt) return 'slow'
    if (dt.daysFrom < 8) return 'quick'
    return "medium"
  }

  openLocationDetail(location: StoreViewInterface) {
    this.router.navigate(['filiale', location.slug])
  }

  isStorePickupPossible(location: StoreViewInterface) {
    if (! this.distinctProduct) return false

    if (! this.storePickupEnabled
      || ! this.distinctProduct.product.isStorePickupAllowed(this.distinctProduct.selectedVariant)
      || ! location.storePickupPossible
    ) return false

    // when online on stock, the product will be sent to the store, so store pickup is possible
    const s = this.distinctProduct.product.getStock(this.distinctProduct?.selectedVariant ?? 0, this.distinctProduct?.distinctId)
    if (s && s > 0) return true

    if (location.stock < this.quantity && ! this.distinctProduct.product.isPreorderPossible(this.distinctProduct.selectedVariant)) {
      return false
    }
    
    return true
  }

  toggleLocationDetails(location: StoreViewInterface) {
    if (! this.selectedLocation) {
      this.selectedLocation = location
    } else if (! location) {
      this.selectedLocation = undefined
    } else if (location.idPerson == this.selectedLocation.idPerson) {
      this.selectedLocation = undefined
    } else {
      this.selectedLocation = location
    }
    this.showSelectedElementOnTop()
  }

  showSelectedElementOnTop() {
    let that = this
    const idp = this.selectedLocation ? this.selectedLocation?.idPerson : undefined
    if (idp) {
      setTimeout(() => {
        const scrollToEl = that.locationList.nativeElement.querySelector('li.location-' + idp) as HTMLUListElement
        if (scrollToEl) {
          that.locationList.nativeElement.scrollTo({
            top: scrollToEl.offsetTop - scrollToEl.offsetHeight - 24,
          })
        }
      }, 100)
    }
  }

  onSelectLocation(location: StoreViewInterface | undefined) {
    // location finder on product page has always a distinct product set
    if (this.distinctProduct) {
      if (location) {
        if (! this.isStorePickupPossible(location)) return

        this.selectLocationEvent.emit(location)
        this.selectedLocation = location

      } else {
        this.selectLocationEvent.emit(undefined)
        this.selectedLocation = undefined
      }
    } else {
      // global location finder
      if (location) {
        this.toggleLocationDetails(location)
      }
    }
  }

  async onCoordinatesChange(c: CoordinatesChangedInterface) {
    this.currentPosition = c.currentPosition
    await this.ensureLeaflet()
    this.handleLocations(c.shownPositions)
  }
  centerCustomerLocation() {
    if (this.customerLocation && this.showMap) {
      this.mapComponent.leafletMap.setView([this.customerLocation.lat, this.customerLocation.lon])
    }
  }
  setCurrentPosition() {
    if (this.showMap) {
      this.locatorLoading = true
      this.mapComponent.locate(false).subscribe(latlng => {
        this.currentPosition = latlng
        this.handleLocations()
        this.fitNearestLocations()
        this.locatorLoading = false
      }, error => {
        this.locatorLoading = false
      })
    }
  }

  centerLocation(store: StoreViewInterface): void {
    if (this.showMap) {
      this.mapComponent.leafletMap.setView([store.address.lat, store.address.lng])
    }
  }
  searchAddress(formFields: QueryFormRequestInterface): void {
    this.locationService.queryAddress(formFields.query).subscribe((response) => {
      this.queryAddresses = response
    })
  }

  async centerQueryAddress() {
    if (this.customerLocation) {
      await this.ensureLeaflet()
      this.currentPosition = this.leafletLib.latLng(parseFloat(this.customerLocation.lat), parseFloat(this.customerLocation.lon))
      this.handleLocations()
      this.fitNearestLocations()
    }
  }

  async selectQueryAddress(location: QueryFormLocationInterface) {
    if (! location || ! location.lat || ! location.lon) return
    this.selectedQueryAddressesIndex = 0
    this.customerLocation = location
    this.queryAddresses = []
    await this.ensureLeaflet()
    this.currentPosition = this.leafletLib.latLng(parseFloat(location.lat), parseFloat(location.lon))

    this.handleLocations()
    this.fitNearestLocations()
  }

  fitNearestLocations() {
    if (! this.mapComponent) return
    // zoom to location with showing at least 3 stores on the map
    let nearestLocations: LatLngLiteral[] = []
    let furtherLocations: LatLngLiteral[] = []

    let i = 0
    for (let location of this.locations()) {
      if (i < 5) {
        if (location.address.lat && location.address.lng) {
          if (i < 3) furtherLocations.push({ lat: location.address.lat, lng: location.address.lng })
          let distance = this.leafletLib.latLng([location.address.lat, location.address.lng]).distanceTo(this.currentPosition)
          if (distance < 50000) {
            nearestLocations.push({ lat: location.address.lat, lng: location.address.lng })
          }
          i++
        }
      }
    }

    if (nearestLocations.length == 0) {
      nearestLocations = furtherLocations
    }

    if (this.currentPosition) nearestLocations.push(this.currentPosition)
    let leafletBounds: LatLngBoundsExpression = this.leafletLib.latLngBounds(nearestLocations)

    this.latestLeafletBounds = leafletBounds

    // center and fit to bounds
    // let ne = leafletBounds.getNorthEast()
    // let sw = leafletBounds.getSouthWest()
    //
    // let neSymmetric=[ne.lat + (this.currentPosition!.lat - ne.lat) * 2, ne.lng + (this.currentPosition!.lng - ne.lng) * 2]
    // let swSymmetric=[sw.lat + (this.currentPosition!.lat - sw.lat) * 2, sw.lng + (this.currentPosition!.lng - sw.lng) * 2]
    // leafletBounds.extend(this.leafletLib.latLngBounds(swSymmetric, neSymmetric))

    this.mapComponent.leafletMap.fitBounds(leafletBounds)
  }

  navigateQueryAddresses(e: KeyboardEvent): void {
    if (e.key == 'ArrowDown') {
      if (this.selectedQueryAddressesIndex < this.queryAddresses.length-1) {
        this.selectedQueryAddressesIndex++
      }
    } else if (e.key == 'ArrowUp') {
      if (this.selectedQueryAddressesIndex > 0) {
        this.selectedQueryAddressesIndex--
      }
    } else if (e.key == 'Enter') {
      this.selectQueryAddress(this.queryAddresses[this.selectedQueryAddressesIndex])
    } else if (e.key == 'Escape') {
      this.selectedQueryAddressesIndex = 0
      this.queryAddresses = []
    }
  }

  private async ensureLeaflet() {
    if (!this.leafletLib && this.leafletReady) {
      await this.leafletReady
    }
  }

  handleLocations(filterCoords?: LatLng[] | LatLngLiteral[]) {
    let locs = [ ... this.locations() ]

    if (this.currentPosition && this.leafletLib) {
      for (let location of locs) {
        try {
          if (location.address.lat && location.address.lng) {
            location.distance = this.leafletLib.latLng([location.address.lat, location.address.lng]).distanceTo(this.currentPosition)
            if (filterCoords) {
              if (filterCoords.length === 0) {
                location.filtered = true
              } else {
                for (let coord of filterCoords) {
                  if (coord.lat == location.address.lat && coord.lng == location.address.lng) {
                    location.filtered = false
                    break
                  } else location.filtered = true
                }
              }
            } else {
              location.filtered = undefined
            }
          }
        } catch (e) {
          console.error('handleLocations error in loop:', e)
        }
      }
    }
    
    locs.sort((a: StoreViewInterface, b: StoreViewInterface) => {
      if (this.distinctProduct) {
        if (a.storePickupPossible && ! b.storePickupPossible) return -2
        if (! a.storePickupPossible && b.storePickupPossible) return 2

        if (a.stock > 0 && b.stock > 0) {
          if (!a.distance || !b.distance) return 0
          return a.distance - b.distance
        } else if (a.stock > 0) {
          return -1
        } else if (b.stock > 0) {
          return 1
        }
      }

      if (!a.distance || !b.distance) return 0
      if (a.distance < b.distance) return -1
      if (a.distance > b.distance) return 1
      
      return 0
    })

    this.locations.set(locs)

    setTimeout(() => {
      this.showSelectedElementOnTop()
    }, 100)
  }

  ngOnDestroy(): void {
    if (isPlatformBrowser(this.platformId)) {
      window.clearInterval(this.dateInterval)
    }
  }
}
